import { AppData, Metadata, Registry } from '@lightningjs/sdk';
import Conviva, {
  AdAnalytics,
  ConvivaMetadata,
  ConvivaDeviceMetadata,
  VideoAnalytics,
} from '@convivainc/conviva-js-coresdk';
import { EpgChannel, LiveEventStream, Video } from 'types/api/media';
import {
  getCurrentEpgProgram,
  isLiveEvent,
  isEpgChannel,
} from 'support/contentUtils';
import { DeviceInfo } from 'models/platforms/deviceInfo';
import { getBaseDomainFromUrl, roundMilliseconds } from 'support/generalUtils';
import { ViewContext } from 'types/analytics';
import StorageService from 'services/StorageService';
import { Ad } from 'types/player';
import { AdType } from './reportingServiceVideo';
import { Manufacturer } from 'models/platforms/manufacturer';
import LoginRadiusService from 'services/LoginRadiusService';

type BitRateData = {
  averageBitRate: number;
  lastBitRateMeasure: number;
  firstBitRateMeasure: number;
  notMonitoredTime: number;
};

const CUSTOM_EVENTS = {
  AD_EXIT: 'Ad_Exit',
};

export default class ConvivaVideo {
  private readonly storageService: StorageService;
  private readonly loginRadiusService: LoginRadiusService | null;

  private _videoAnalytics: Conviva.VideoAnalytics | undefined;
  private _adAnalytics: Conviva.AdAnalytics | undefined;
  private _isAdPlaying = false;
  private _isSeeking = false;
  private _currentBitRate: null | number = null;
  private _bitRateData: null | BitRateData = null;
  private _adBitRateData: null | BitRateData = null;

  private defaultCustomMetadata = {
    appVersion: Metadata.appVersion() ?? '',
    ...(!!AppData?.device.getSubmittedAppVersion()
      ? { submittedAppVersion: AppData?.device.getSubmittedAppVersion() }
      : {}),
    brand: AppData!.conviva.brand,
    [Conviva.Constants.APPLICATION_VERSION]: Metadata.appVersion() ?? '',
    'c3.cm.brand': AppData!.conviva.brand,
  };

  constructor(
    deviceInfo: DeviceInfo,
    storageService: StorageService,
    loginRadiusService: LoginRadiusService | null,
  ) {
    if (AppData === undefined) {
      throw new Error('AppData not set');
    }

    this.storageService = storageService;
    this.loginRadiusService = loginRadiusService;

    // this is modified from https://pulse.conviva.com/learning-center/content/sensor_developer_center/sensor_integration/javascript/javascript_stream_sensor.htm
    const callbackFunctions: Conviva.ConvivaUtils = {
      [Conviva.Constants.CallbackFunctions.CONSOLE_LOG]: (
        message,
        logLevel,
      ) => {
        if (typeof console === 'undefined') return;
        if (
          console.log &&
          (logLevel === Conviva.Constants.LogLevel.DEBUG ||
            logLevel === Conviva.Constants.LogLevel.INFO)
        ) {
          console.log(message);
        } else if (
          console.warn &&
          logLevel === Conviva.Constants.LogLevel.WARNING
        ) {
          console.warn(message);
        } else if (
          console.error &&
          logLevel === Conviva.Constants.LogLevel.ERROR
        ) {
          console.error(message);
        }
      },

      [Conviva.Constants.CallbackFunctions.MAKE_REQUEST]: (
        httpMethod,
        url,
        data,
        contentType,
        timeoutMs,
        callback,
      ) => {
        const xmlHttpReq = new XMLHttpRequest();

        xmlHttpReq.open(httpMethod, url, true);

        if (contentType && xmlHttpReq.overrideMimeType) {
          // @ts-ignore
          xmlHttpReq.overrideMimeType = contentType;
        }
        if (contentType && xmlHttpReq.setRequestHeader) {
          xmlHttpReq.setRequestHeader('Content-Type', contentType);
        }
        if (timeoutMs > 0) {
          xmlHttpReq.timeout = timeoutMs;
          xmlHttpReq.ontimeout = function () {
            // Often this callback will be called after onreadystatechange.
            // The first callback called will cleanup the other to prevent duplicate responses.
            xmlHttpReq.ontimeout = xmlHttpReq.onreadystatechange = null;
            if (callback) callback(false, 'timeout after ' + timeoutMs + ' ms');
          };
        }

        xmlHttpReq.onreadystatechange = function () {
          if (xmlHttpReq.readyState === 4) {
            xmlHttpReq.ontimeout = xmlHttpReq.onreadystatechange = null;
            if (xmlHttpReq.status == 200) {
              if (callback) callback(true, xmlHttpReq.responseText);
            } else {
              if (callback) callback(false, 'http status ' + xmlHttpReq.status);
            }
          }
        };

        xmlHttpReq.send(data);
      },

      [Conviva.Constants.CallbackFunctions.SAVE_DATA]: (
        storageSpace,
        storageKey,
        data,
        callback,
      ) => {
        try {
          window.localStorage.setItem(`${storageSpace}.${storageKey}`, data);
          callback(true, null as unknown as string);
        } catch (e: any) {
          callback(false, e.toString());
        }
      },

      [Conviva.Constants.CallbackFunctions.LOAD_DATA]: (
        storageSpace,
        storageKey,
        callback,
      ) => {
        try {
          const data =
            window.localStorage.getItem(`${storageSpace}.${storageKey}`) ?? '';
          callback(true, data);
        } catch (e: any) {
          callback(false, e.toString());
        }
      },

      [Conviva.Constants.CallbackFunctions.GET_EPOCH_TIME_IN_MS]: () => {
        const d = new Date();
        return d.getTime();
      },

      [Conviva.Constants.CallbackFunctions.CREATE_TIMER]: (
        timerAction,
        intervalMs,
      ) => {
        let timerId = Registry.setInterval(timerAction, intervalMs);
        const cancelTimerFunc = function () {
          if (timerId !== -1) {
            Registry.clearInterval(timerId);
            timerId = -1;
          }
        };
        return cancelTimerFunc;
      },
    };

    // Initialize Conviva
    const settings: Conviva.ConvivaOptions = {};
    if (!AppData.isProduction)
      settings[Conviva.Constants.GATEWAY_URL] = AppData.conviva.gatewayUrl;
    if (AppData.debug)
      settings[Conviva.Constants.LOG_LEVEL] = Conviva.Constants.LogLevel.DEBUG;

    Conviva.Analytics.init(
      AppData.conviva.customerKey,
      callbackFunctions,
      settings,
    );

    let deviceCategory: ConvivaDeviceMetadata['DeviceCategory'];
    switch (deviceInfo.manufacturer) {
      case Manufacturer.SAMSUNG:
        deviceCategory = Conviva.Constants.DeviceCategory.SAMSUNG_TV;
        break;
      case Manufacturer.LG:
        deviceCategory = Conviva.Constants.DeviceCategory.LG_TV;
        break;
      case Manufacturer.VIZIO:
      default:
        deviceCategory = Conviva.Constants.DeviceCategory.SMART_TV;
    }

    // Setup Metadata
    const deviceMetadata: ConvivaDeviceMetadata = {
      [Conviva.Constants.DeviceMetadata.CATEGORY]: deviceCategory,
      [Conviva.Constants.DeviceMetadata.BRAND]: deviceInfo.manufacturer,
      [Conviva.Constants.DeviceMetadata.MODEL]: deviceInfo.model,
      [Conviva.Constants.DeviceMetadata.OS_VERSION]: deviceInfo.osVersion,
      [Conviva.Constants.DeviceMetadata.TYPE]:
        Conviva.Constants.DeviceType.SMARTTV,
    };
    Conviva.Analytics.setDeviceMetadata(deviceMetadata);
  }

  private accountMetadata(): Record<string, string> {
    const uid = this.loginRadiusService?.getAccount()?.uid;
    return uid ? { cwLoginID: uid } : {};
  }

  private getVideoPlaybackData(
    content: Video,
    viewContext: ViewContext | null,
  ): ConvivaMetadata {
    const constants = AppData!.conviva;

    const customMetadata: Record<string, string> = {
      ...this.defaultCustomMetadata,
      ...this.accountMetadata(),
      ['c3.cm.categoryType']: content.seriesType || 'Null',
      ['c3.cm.contentType']: content.contentType || '*null',
      ['c3.cm.episodeNumber']: content.episode,
      ['c3.cm.genre']: content.genre || 'Null',
      ['c3.cm.genreList']: content.genre,
      ['c3.cm.id']: content.guid,
      ['c3.cm.seasonNumber']: content.season || '*null',
      ['c3.cm.seriesName']: content.seriesName,
      ['c3.cm.showTitle']: content.title,
      assetId: content.guid,
      contentType: content.contentType || '*null',
      episodeNumber: content.episode,
      publishDate: content.startTime,
      seasonNumber: content.season || '*null',
      seriesTitle: content.seriesName,
      tmsId: content.tmsId,
      viewContext: viewContext ?? '*null',
    };

    const contentInfo = {
      [Conviva.Constants.ASSET_NAME]: content.title,
      [Conviva.Constants.IS_LIVE]: Conviva.Constants.StreamType.VOD,
      [Conviva.Constants.PLAYER_NAME]: constants.playerName,
      [Conviva.Constants.DURATION]: Number(content.durationSecs),
    } as ConvivaMetadata;

    return { ...contentInfo, ...customMetadata };
  }

  private getLiveVideoPlaybackData(
    content: LiveEventStream,
    viewContext: ViewContext | null,
  ): ConvivaMetadata {
    const constants = AppData!.conviva;

    const customMetadata: Record<string, string> = {
      ...this.defaultCustomMetadata,
      ...this.accountMetadata(),
      ['c3.cm.categoryType']: content.type || 'Null',
      ['c3.cm.contentType']: content.type || '*null',
      ['c3.cm.genre']: content.analytics.comscoreGenre || 'Null',
      ['c3.cm.id']: content.analytics.guid!,
      ['c3.cm.seriesName']: content.analytics.title ?? '*null',
      ['c3.cm.showTitle']: content.title,
      ['c3.video.isLive']: Conviva.Constants.StreamType.LIVE,
      assetId: content.analytics.guid!,
      eventName: content.title,
      publishDate: content.startDateStream,
      seriesTitle: content.analytics.title ?? '*null',
      viewContext: viewContext ?? '*null',
    };

    const contentInfo = {
      [Conviva.Constants.ASSET_NAME]: content.title,
      [Conviva.Constants.IS_LIVE]: Conviva.Constants.StreamType.LIVE,
      [Conviva.Constants.PLAYER_NAME]: constants.playerName,
      [Conviva.Constants.DURATION]: Number(content.durationSecs),
    } as ConvivaMetadata;

    return { ...contentInfo, ...customMetadata };
  }

  private getEpgPlaybackData(
    channel: EpgChannel,
    viewContext: ViewContext | null,
  ): ConvivaMetadata {
    const constants = AppData!.conviva;

    const epgProgram = getCurrentEpgProgram(channel);

    const customMetadata: Record<string, string> = {
      ...this.defaultCustomMetadata,
      ...this.accountMetadata(),
      ['c3.cm.categoryType']: epgProgram?.category || 'Null',
      ['c3.cm.channel']: channel.slug,
      ['c3.cm.contentType']: channel.type || '*null',
      ['c3.cm.genre']: channel.genre || 'Null',
      ['c3.cm.id']: epgProgram?.assetId || '*null',
      ['c3.cm.seriesName']: epgProgram?.title || '*null',
      ['c3.cm.showTitle']: epgProgram?.subtitle || 'Null',
      ['c3.video.isLive']: Conviva.Constants.StreamType.LIVE,
      assetId: epgProgram?.assetId || '*null',
      publishDate: epgProgram?.startTime ?? '*null',
      seriesTitle: epgProgram?.title || '*null',
      viewContext: viewContext ?? '*null',
    };

    const contentInfo = {
      [Conviva.Constants.ASSET_NAME]: epgProgram?.convivaAssetName ?? '*null',
      [Conviva.Constants.IS_LIVE]: Conviva.Constants.StreamType.LIVE,
      [Conviva.Constants.PLAYER_NAME]: constants.playerName,
      [Conviva.Constants.DURATION]: Number(epgProgram?.durationSecs ?? 0),
    } as ConvivaMetadata;

    return { ...contentInfo, ...customMetadata };
  }

  private initializeBitRateData() {
    if (this._currentBitRate === null) return;

    if (this._isAdPlaying) {
      this._adBitRateData = {
        averageBitRate: this._currentBitRate,
        firstBitRateMeasure: Date.now(),
        lastBitRateMeasure: Date.now(),
        notMonitoredTime: 0,
      };
    } else {
      this._bitRateData = {
        averageBitRate: this._currentBitRate,
        firstBitRateMeasure: Date.now(),
        lastBitRateMeasure: Date.now(),
        notMonitoredTime: 0,
      };
    }
  }

  private updateBitRateDataNotMonitored() {
    let bitRateData: BitRateData | null = null;

    if (this._isAdPlaying) {
      bitRateData = this._adBitRateData;
    } else {
      bitRateData = this._bitRateData;
    }

    if (bitRateData === null || this._currentBitRate === null) return;

    const currentTime = Date.now();

    bitRateData.notMonitoredTime +=
      currentTime - bitRateData.lastBitRateMeasure;

    // prevent lastBitRateMeasure being less then firstBitRate + notMonitoredTime
    bitRateData.lastBitRateMeasure = currentTime;
  }

  private updateBitRateData() {
    let bitRateData: BitRateData | null = null;

    if (
      (this._isAdPlaying && this._adBitRateData === null) ||
      (!this._isAdPlaying && this._bitRateData === null)
    ) {
      this.initializeBitRateData();
    }

    if (this._isAdPlaying) {
      bitRateData = this._adBitRateData;
    } else {
      bitRateData = this._bitRateData;
    }

    if (bitRateData === null || this._currentBitRate === null) return;

    // firstMeasure is offset by not monitored time so we don't take ad time into the proportion calculation
    const firstMeasure =
      bitRateData.firstBitRateMeasure + bitRateData.notMonitoredTime;
    const lastMeasure = bitRateData.lastBitRateMeasure;
    const currentTime = Date.now();

    const timeElapsed = currentTime - firstMeasure;

    // if a negligible amount of time has pasted return
    if (roundMilliseconds(timeElapsed) <= 0) {
      return;
    }

    const previousProportion = (lastMeasure - firstMeasure) / timeElapsed;
    const newProportion = (currentTime - lastMeasure) / timeElapsed;

    const previousWeightedAverage =
      bitRateData.averageBitRate * previousProportion;
    const newWeightedAverage = this._currentBitRate * newProportion;

    const averageBitRate = roundMilliseconds(
      previousWeightedAverage + newWeightedAverage,
    );

    if (this._isAdPlaying) {
      this._adBitRateData!.averageBitRate = averageBitRate;
      this._adBitRateData!.lastBitRateMeasure = currentTime;
    } else {
      this._bitRateData!.averageBitRate = averageBitRate;
      this._bitRateData!.lastBitRateMeasure = currentTime;
    }
  }

  releaseAdAnalytics() {
    this._adAnalytics?.release?.();
    this._adAnalytics = undefined;
  }

  releaseVideoAnalytics() {
    this._videoAnalytics?.release?.();
    this._videoAnalytics = undefined;
  }

  release() {
    this.releaseAdAnalytics();
    this.releaseVideoAnalytics();
    Conviva.Analytics.release();
  }

  setStreamUrl(url: string) {
    this._videoAnalytics?.setContentInfo({
      [Conviva.Constants.STREAM_URL]: url,
    });
  }

  setContentInfo(
    content: Video | LiveEventStream | EpgChannel,
    viewContext: ViewContext | null,
    videoSessionId: string,
  ) {
    this._bitRateData = null;
    this._adBitRateData = null;

    let metadata: ConvivaMetadata = {};

    if (isEpgChannel(content)) {
      metadata = this.getEpgPlaybackData(content as EpgChannel, viewContext);
    } else if (isLiveEvent(content)) {
      metadata = this.getLiveVideoPlaybackData(
        content as LiveEventStream,
        viewContext,
      );
    } else {
      metadata = this.getVideoPlaybackData(content as Video, viewContext);
    }

    const mpUserId = AppData!.mParticle.mpid;
    if (mpUserId) {
      metadata.cwViewerId = mpUserId;
      metadata[Conviva.Constants.VIEWER_ID] = mpUserId;
    }

    metadata.external_session_id = videoSessionId;

    this._videoAnalytics?.setContentInfo(metadata);
  }

  getAdContentInfo(ad: Ad) {
    const constants = AppData!.conviva;
    return {
      [Conviva.Constants.ASSET_NAME]: ad.getTitle(),
      [Conviva.Constants.IS_LIVE]: Conviva.Constants.StreamType.VOD,
      [Conviva.Constants.PLAYER_NAME]: constants.playerName,
      [Conviva.Constants.DURATION]: Number(ad.getDuration()),
    } as ConvivaMetadata;
  }

  getAdMetadata(ad: Ad, adType: AdType) {
    let adPosition: (typeof Conviva.Constants.AdPosition)[keyof typeof Conviva.Constants.AdPosition];
    switch (adType) {
      case 'pre-roll':
        adPosition = Conviva.Constants.AdPosition.PREROLL;
        break;
      case 'mid-roll':
        adPosition = Conviva.Constants.AdPosition.MIDROLL;
        break;
      case 'post-roll':
        adPosition = Conviva.Constants.AdPosition.POSTROLL;
        break;
    }

    return {
      'c3.ad.technology': Conviva.Constants.AdType.SERVER_SIDE,
      'c3.ad.id': ad.getAdId(),
      'c3.ad.system': ad.getAdSystem() || 'NA',
      'c3.ad.position': adPosition,
      'c3.ad.isSlate': 'false',
      'c3.ad.mediaFileApiFramework': ad.getApiFramework() || 'NA',
      'c3.ad.adStitcher': ad.adStitcher?.() || 'Google DAI', // if we don't provide an ad stitcher function we assume its IMA
      'c3.ad.firstAdSystem': ad.getWrapperAdSystems()[0] || 'NA',
      'c3.ad.firstAdId': ad.getWrapperAdIds()[0] || 'NA',
      'c3.ad.firstCreativeId': ad.getWrapperCreativeIds()[0] || 'NA',
      'c3.ad.creativeId': ad.getCreativeId() || 'NA',
      'c3.ad.sequence': `${ad.getAdPodInfo().getAdPosition()}`,
    } as ConvivaMetadata;
  }

  setAdInfo(ad: Ad, adType: AdType) {
    const adContentInfo = this.getAdContentInfo(ad);
    const adMetadata = this.getAdMetadata(ad, adType);
    this._adAnalytics?.setAdInfo({
      ...adContentInfo,
      ...adMetadata,
    });
  }

  reportContentPlayback(
    content: Video | LiveEventStream | EpgChannel,
    viewContext: ViewContext | null,
    videoSessionId: string,
  ) {
    if (this._videoAnalytics) this.releaseVideoAnalytics();
    this._videoAnalytics = Conviva.Analytics.buildVideoAnalytics();

    this.setContentInfo(content, viewContext, videoSessionId);

    this._videoAnalytics?.reportPlaybackRequested();
  }

  setVideoPlayer(framework: 'HTML5') {
    const playerInfo = this.getPlayerInfo(framework);
    this._videoAnalytics?.setPlayerInfo(playerInfo);
  }

  setAdListener(framework: 'HTML5') {
    if (this._adAnalytics) this.releaseAdAnalytics();
    this._adAnalytics = Conviva.Analytics.buildAdAnalytics(
      this._videoAnalytics,
    );

    const playerInfo = this.getPlayerInfo(framework);
    this._adAnalytics.setAdPlayerInfo(playerInfo);
  }

  // TODO: Add VideoJS framework
  private getPlayerInfo(framework: 'HTML5') {
    const playerInfo: Conviva.ConvivaPlayerInfo = {
      [Conviva.Constants.FRAMEWORK_NAME]: framework,
    };
    if (framework === 'HTML5') {
      playerInfo[Conviva.Constants.FRAMEWORK_VERSION] = 'Unknown';
    }
    return playerInfo;
  }

  reportPlaybackFailed(errorMessage: string) {
    if (this._isAdPlaying) this._adAnalytics?.reportAdFailed(errorMessage);
    this._videoAnalytics?.reportPlaybackFailed(errorMessage);
  }

  reportPlaybackWarning(errorMessage: string) {
    if (this._isAdPlaying) {
      this._adAnalytics?.reportAdError(
        errorMessage,
        Conviva.Constants.ErrorSeverity.WARNING,
      );
    } else {
      this._videoAnalytics?.reportPlaybackError(
        errorMessage,
        Conviva.Constants.ErrorSeverity.WARNING,
      );
    }
  }

  reportStart() {
    this.reportPlayerStateChange('PLAYING');
  }

  reportResume() {
    if (this._isSeeking) return;
    this.reportPlayerStateChange('PLAYING');
  }

  reportPause() {
    this.reportPlayerStateChange('PAUSED');
  }

  reportBufferStart() {
    this.reportPlayerStateChange('BUFFERING');
  }

  reportBufferEnd(resumePlayback: boolean) {
    const newPlayerState: keyof typeof Conviva.Constants.PlayerState =
      resumePlayback ? 'PLAYING' : 'PAUSED';
    this.reportPlayerStateChange(newPlayerState);
  }

  reportSeekStart(contentTime: number) {
    this._isSeeking = true;
    this.reportMetric(Conviva.Constants.Playback.SEEK_STARTED, contentTime);
  }

  reportSeekEnd() {
    this._isSeeking = false;
    this.reportMetric(Conviva.Constants.Playback.SEEK_ENDED);
  }

  reportPlaybackEnded(isAdPlaying: boolean) {
    if (isAdPlaying) this._adAnalytics?.reportAdEnded();
    this._videoAnalytics?.reportPlaybackEnded();
    this._isAdPlaying = false;
    this._isSeeking = false;

    this.releaseAdAnalytics();
    this.releaseVideoAnalytics();
  }

  private reportPlayerStateChange(
    stateKey: keyof typeof Conviva.Constants.PlayerState,
  ) {
    const newState = Conviva.Constants.PlayerState[stateKey];
    this.reportMetric(Conviva.Constants.Playback.PLAYER_STATE, newState);
  }

  reportCdn(url: string) {
    const baseDomain = getBaseDomainFromUrl(url);
    this._videoAnalytics?.reportPlaybackMetric(
      Conviva.Constants.Playback.CDN_IP,
      baseDomain,
    );
  }

  reportBitrate(bitrate: number) {
    this._currentBitRate = bitrate;

    this.reportMetric(Conviva.Constants.Playback.BITRATE, bitrate);
    this.reportAverageBitRate();
  }

  reportAdPodStart() {
    this._videoAnalytics?.reportAdBreakStarted(
      Conviva.Constants.AdType.SERVER_SIDE,
      Conviva.Constants.AdPlayer.CONTENT,
    );
    this._isAdPlaying = true;
  }

  reportAdMetadata(ad: Ad, type: AdType) {
    this.setAdInfo(ad, type);
    this._adAnalytics?.reportAdLoaded();
  }

  reportAdStart(ad?: Ad, adType?: AdType) {
    if (!ad || !adType) return;
    this._adAnalytics?.reportAdStarted();
    this.reportPlayerStateChange('PLAYING');
  }

  reportAdEnd() {
    this._adAnalytics?.reportAdEnded();
  }

  reportAdPodEnd(skipped: boolean) {
    if (skipped) {
      this._adAnalytics?.reportAdSkipped();
    } else {
      this._adAnalytics?.reportAdEnded();
    }

    this._videoAnalytics?.reportAdBreakEnded();
    this._isAdPlaying = false;
    this._adBitRateData = null;

    this.updateBitRateDataNotMonitored();
  }

  reportInteractiveAdExit(exitType: string) {
    const metadata = {
      Exit_Type: exitType,
    };
    this._videoAnalytics?.reportPlaybackEvent(CUSTOM_EVENTS.AD_EXIT, metadata);
  }

  reportBackgrounded() {
    Conviva.Analytics.reportAppBackgrounded();
  }

  reportForegrounded() {
    Conviva.Analytics.reportAppForegrounded();
  }

  handleHeartbeat(relativeTime: number) {
    this.reportMetric(Conviva.Constants.Playback.PLAY_HEAD_TIME, relativeTime);
    this.reportAverageBitRate();
  }

  private reportAverageBitRate() {
    this.updateBitRateData();

    if (this._isAdPlaying && this._adBitRateData !== null) {
      this._adAnalytics?.reportAdMetric(
        Conviva.Constants.Playback.AVG_BITRATE,
        this._adBitRateData!.averageBitRate,
      );
    } else if (this._bitRateData !== null) {
      this._videoAnalytics?.reportPlaybackMetric(
        Conviva.Constants.Playback.AVG_BITRATE,
        this._bitRateData!.averageBitRate,
      );
    }
  }

  private reportMetric(
    ...params: Parameters<
      AdAnalytics['reportAdMetric'] & VideoAnalytics['reportPlaybackMetric']
    >
  ) {
    if (this._isAdPlaying) {
      this._adAnalytics?.reportAdMetric(...params);
    } else {
      this._videoAnalytics?.reportPlaybackMetric(...params);
    }
  }
}
